import { z } from 'zod';
import { USER_ROLES } from './constants.js';
/**
 * Tools a model can call during a reply (v0.8). The server registry holds the
 * executable definitions; this module holds what clients and every renderer
 * (conversation, share links, exports) need to describe a tool step.
 */
/** `read` tools look something up; `write` tools change something elsewhere and always need approval. */
export const TOOL_KINDS = ['read', 'write'];
/**
 * Built-in tool ids, and the shape of a connector tool id
 * (`mcp__<connector>__<tool>`). Every id is also the function name sent to the
 * provider, so it keeps to the characters and length OpenAI and Anthropic
 * accept (`^[A-Za-z0-9_-]{1,64}$`). A connector slug has no underscores, so
 * the first `__` after `mcp__` always ends it.
 */
export const TOOL_ID_PATTERN = /^(?:[a-z][a-z0-9_]{0,63}|mcp__[a-z0-9-]{1,24}__[A-Za-z0-9_-]{1,57})$/;
export const MAX_TOOL_ID_LENGTH = 64;
/** A connector's slug: lowercase letters, digits and inner hyphens, at most 24 characters. */
export const CONNECTOR_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,22}[a-z0-9])?$/;
/** `mcp__<slug>__<key>`: the id of a connector tool. */
export function connectorToolId(slug, key) {
    return `mcp__${slug}__${key}`;
}
/** The connector slug a tool id belongs to, or null for a built-in tool. */
export function connectorSlugOfToolId(toolId) {
    const match = /^mcp__([a-z0-9-]{1,24})__./.exec(toolId);
    return match?.[1] ?? null;
}
/** Model steps per reply: default and the range an administrator may choose. */
export const DEFAULT_MAX_TOOL_STEPS = 8;
export const MIN_TOOL_STEPS = 1;
export const MAX_TOOL_STEPS = 20;
/** Reason sent to the model when an approval was never answered. */
export const APPROVAL_NOT_ANSWERED = 'not answered';
/**
 * Whether a role may use a tool when nobody has saved a choice for it. Built-in
 * read tools are on for every role except `restricted`; anything else (write
 * tools, connector tools) is off until an administrator allows it.
 */
export function defaultToolAllowed(role, tool) {
    return tool.source === 'builtin' && tool.kind === 'read' && role !== 'restricted';
}
/** Body of `PUT /admin/roles/:role/tools`: only sent tools change. */
export const updateRoleToolsSchema = z
    .object({
    tools: z
        .record(z.string().max(MAX_TOOL_ID_LENGTH).regex(TOOL_ID_PATTERN, 'Unknown tool id'), z.boolean())
        .refine((tools) => Object.keys(tools).length > 0, { message: 'Send at least one change.' })
        .refine((tools) => Object.keys(tools).length <= 200, { message: 'Too many tools.' }),
})
    .strict();
export const roleToolSchema = z.object({
    id: z.string(),
    label: z.string(),
    kind: z.enum(TOOL_KINDS),
    source: z.enum(['builtin', 'connector']),
    allowed: z.boolean(),
    /** The connector's name, for grouping connector tools; absent for built-in tools. */
    connector: z.string().nullable().optional(),
});
export const roleToolsResponseSchema = z.object({
    role: z.enum(USER_ROLES),
    tools: z.array(roleToolSchema),
});
/** Body of `POST /api/chat/:threadId/approvals`: answers to every open approval of one reply. */
export const answerToolApprovalsSchema = z
    .object({
    messageId: z.string().min(1).max(200),
    responses: z
        .array(z
        .object({
        approvalId: z.string().min(1).max(200),
        approved: z.boolean(),
    })
        .strict())
        .min(1)
        .max(32),
    /** The person's time zone, as when sending a message (#248). */
    timeZone: z.string().max(100).optional(),
})
    .strict();
/** Why a reply's tool loop ended early, stored as a `data-tool-limit` part. */
export const TOOL_LIMIT_REASONS = ['steps', 'allowance', 'context'];
export function toolLimitNote(reason, steps) {
    if (reason === 'allowance')
        return 'This reply stopped using tools because your usage allowance ran out.';
    if (reason === 'context')
        return 'This reply stopped using tools because the results no longer fit the model’s input limit.';
    return `This reply reached the limit of ${steps ?? DEFAULT_MAX_TOOL_STEPS} tool steps, so it answered with what it had found.`;
}
/** Labels for tools this release knows about; unknown ids fall back to the id. */
const KNOWN_TOOL_LABELS = {
    web_search: 'Web search',
    remember: 'Memory',
    forget: 'Memory',
    create_artifact: 'Create artifact',
    update_artifact: 'Update artifact',
};
/** Built-in tools that change only OCI's own artifacts (v0.9); see `ARTIFACT_TOOL_IDS`. */
export const ARTIFACT_TOOL_IDS = ['create_artifact', 'update_artifact'];
/** How a declined `create_artifact` call's note to the model begins (#149, #201). */
export const ARTIFACT_NOT_SAVED = 'Not saved as an artifact';
/**
 * A `create_artifact` call the server declined (#149) because its content
 * belongs in the reply: not a failure, and not something the person did, so
 * the conversation, share pages and downloads leave it out (#201). Replies
 * stored before #201 have the decline as the step's error text.
 */
export function isDeclinedArtifactPart(part) {
    if (!isToolPart(part) || toolIdOfPart(part) !== 'create_artifact')
        return false;
    if (part.state === 'output-available')
        return part.output?.saved === false;
    return (part.state === 'output-error' &&
        typeof part.errorText === 'string' &&
        part.errorText.startsWith(`${ARTIFACT_NOT_SAVED}:`));
}
/** The artifact a finished artifact tool step created or changed, from its result. */
export function artifactOfToolPart(part) {
    const toolId = toolIdOfPart(part);
    if (!ARTIFACT_TOOL_IDS.includes(toolId))
        return null;
    if (part.state !== 'output-available')
        return null;
    const output = part.output;
    if (typeof output?.artifactId !== 'string' || typeof output.version !== 'number')
        return null;
    return {
        artifactId: output.artifactId,
        title: typeof output.title === 'string' ? output.title : 'Artifact',
        version: output.version,
    };
}
export function toolLabel(toolId) {
    return KNOWN_TOOL_LABELS[toolId] ?? toolId;
}
/** True for a stored or streaming SDK tool part (`tool-<id>` or `dynamic-tool`). */
export function isToolPart(part) {
    if (typeof part !== 'object' || part === null)
        return false;
    const candidate = part;
    return (typeof candidate.type === 'string' &&
        (candidate.type.startsWith('tool-') || candidate.type === 'dynamic-tool') &&
        typeof candidate.toolCallId === 'string');
}
export function toolIdOfPart(part) {
    if (part.type === 'dynamic-tool')
        return typeof part.toolName === 'string' ? part.toolName : '';
    return String(part.type).slice('tool-'.length);
}
function stepState(part) {
    switch (part.state) {
        case 'approval-requested':
            return 'awaiting-approval';
        case 'approval-responded':
            return part.approval?.approved === true
                ? 'approved'
                : 'denied';
        case 'output-available':
            return 'done';
        case 'output-error':
            return 'error';
        case 'output-denied':
            return 'denied';
        default:
            return 'running';
    }
}
const quote = (value) => {
    const trimmed = value.replace(/\s+/g, ' ').trim();
    return `'${trimmed.length > 80 ? `${trimmed.slice(0, 79)}…` : trimmed}'`;
};
function inputQuery(part) {
    const input = part.input;
    return typeof input?.query === 'string' && input.query.trim() ? input.query : null;
}
function resultCount(part) {
    const output = part.output;
    return Array.isArray(output?.results) ? output.results.length : null;
}
/** Summarise one tool part. Results are reduced to counts; inputs to a short phrase. */
export function summarizeToolPart(part) {
    const toolId = toolIdOfPart(part);
    // The server streams each tool's label as the part's title.
    const label = typeof part.title === 'string' && part.title.trim()
        ? part.title.trim().slice(0, 120)
        : toolLabel(toolId);
    const state = stepState(part);
    const approval = part.approval;
    let summary;
    if (toolId === 'web_search') {
        const query = inputQuery(part);
        const target = query ? ` for ${quote(query)}` : '';
        if (state === 'done') {
            const count = resultCount(part) ?? 0;
            // v0.10: when the fallback provider answered, say which one.
            const output = part.output;
            const via = output?.fallback === true && typeof output.provider === 'string' && output.provider
                ? ` · via ${output.provider} (fallback)`
                : '';
            summary = `Searched the web${target} · ${count} ${count === 1 ? 'result' : 'results'}${via}`;
        }
        else if (state === 'error')
            summary = `Web search${target} failed`;
        else if (state === 'denied')
            summary = `Web search${target} was not run`;
        else
            summary = `Searching the web${target}`;
    }
    else if (toolId === 'remember' || toolId === 'forget') {
        const output = part.output;
        const input = part.input;
        const text = typeof output?.content === 'string'
            ? output.content
            : typeof input?.content === 'string'
                ? input.content
                : '';
        const target = text.trim() ? ` ${quote(text)}` : '';
        const remember = toolId === 'remember';
        if (state === 'done')
            summary =
                output?.action === 'exists'
                    ? `Already remembered${target}`
                    : remember
                        ? `Remembered${target}`
                        : `Forgot${target}`;
        else if (state === 'error')
            summary = remember ? 'Saving a memory failed' : 'Forgetting failed';
        else if (state === 'denied')
            summary = 'Memory was not changed';
        else
            summary = remember ? 'Saving a memory' : 'Forgetting a memory';
    }
    else if (ARTIFACT_TOOL_IDS.includes(toolId)) {
        const created = toolId === 'create_artifact';
        const result = artifactOfToolPart(part);
        const input = part.input;
        const title = result?.title ?? (typeof input?.title === 'string' ? input.title : null);
        const target = title ? ` ${quote(title)}` : '';
        if (isDeclinedArtifactPart(part))
            summary = `${ARTIFACT_NOT_SAVED}${target}`;
        else if (state === 'done' && result)
            summary = created
                ? `Created artifact${target}`
                : `Updated artifact${target} · version ${result.version}`;
        else if (state === 'error')
            summary = `${created ? 'Creating' : 'Updating'} an artifact failed`;
        else if (state === 'denied')
            summary = `${label} was not run`;
        else
            summary = created ? `Creating artifact${target}` : 'Updating an artifact';
    }
    else if (state === 'done')
        summary = `Used ${label}`;
    else if (state === 'error')
        summary = `${label} failed`;
    else if (state === 'denied')
        summary = `${label} was not run`;
    else if (state === 'awaiting-approval')
        summary = `${label} is waiting for your approval`;
    else
        summary = `Using ${label}`;
    const reason = typeof approval?.reason === 'string' ? approval.reason : null;
    if (state === 'denied' && reason)
        summary = `${summary} (${reason})`;
    return {
        toolCallId: String(part.toolCallId),
        toolId,
        label,
        state,
        summary,
        approvalId: state === 'awaiting-approval' && typeof approval?.id === 'string' ? approval.id : null,
        reason,
    };
}
/** Every tool step of a message, in order, without declined artifact attempts (#201). */
export function toolStepsOf(parts) {
    if (!Array.isArray(parts))
        return [];
    return parts
        .filter((part) => isToolPart(part) && !isDeclinedArtifactPart(part))
        .map((part) => summarizeToolPart(part));
}
//# sourceMappingURL=tools.js.map